import axios from 'axios'
import { Alert } from 'components'

const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080/api'
const BACKEND_ACCESS_KEY = process.env.REACT_APP_BACKEND_ACCESS_KEY

// Cache storage
const cache = new Map()
const pendingRequests = new Map()

// Throttling configuration
const throttleConfig = {
  default: { limit: 10, window: 60000 }, // 10 requests per minute
  search: { limit: 5, window: 30000 },   // 5 requests per 30 seconds
  upload: { limit: 3, window: 60000 },   // 3 requests per minute
  critical: { limit: 20, window: 60000 } // 20 requests per minute
}

// Request tracking for throttling
const requestHistory = new Map()

/**
 * Enhanced API handler with caching, throttling, and error handling
 */
const enhancedHandler = ({ 
  baseURL = API_HOST, 
  url = null, 
  method = 'get', 
  args = {}, 
  isFile = false,
  cacheDuration = 300000, // 5 minutes default
  throttleType = 'default',
  retryAttempts = 3,
  retryDelay = 1000,
  signal = null
}) => {
  const request = {}

  if (baseURL) request.baseURL = baseURL
  if (url) request.url = url
  request.method = method

  // Add authorization header
  if (BACKEND_ACCESS_KEY) {
    request.headers = {
      ...request.headers,
      Authorization: `Bearer ${BACKEND_ACCESS_KEY}`,
      BackendAccessKey: BACKEND_ACCESS_KEY,
      backendaccesskey: BACKEND_ACCESS_KEY,
      Backendaccesskey: BACKEND_ACCESS_KEY
    }
  }

  // Handle file uploads
  // For file uploads, DO NOT set Content-Type manually
  // Let axios automatically set it with the proper boundary parameter
  if (!isFile) {
    request.headers = {
      ...request.headers,
      'Content-Type': 'application/json'
    }
  }

  if (request.method === 'get') {
    request.params = args
  } else {
    request.data = args
  }

  // Add signal for request cancellation if provided
  if (signal) {
    request.signal = signal
  }

  // Check cache for GET requests
  const cacheKey = method === 'get' ? `${method}:${url}:${JSON.stringify(args)}` : null
  const cacheDurationToUse = method === 'get' ? cacheDuration : 0

  if (cacheKey && cache.has(cacheKey)) {
    const cachedData = cache.get(cacheKey)
    if (Date.now() - cachedData.timestamp < cacheDurationToUse) {
      console.log('📦 Serving from cache:', cacheKey)
      return Promise.resolve(cachedData.data)
    } else {
      cache.delete(cacheKey)
    }
  }

  // Check throttling
  const throttleResult = checkThrottling(throttleType)
  if (!throttleResult.allowed) {
    const error = new Error(`Rate limit exceeded. Please wait ${Math.ceil(throttleResult.waitTime / 1000)} seconds.`)
    error.code = 'RATE_LIMIT_EXCEEDED'
    return Promise.reject(error)
  }

  // Check for pending requests
  if (pendingRequests.has(cacheKey)) {
    console.log('🔄 Request already pending, returning existing promise')
    return pendingRequests.get(cacheKey)
  }

  // Create request promise
  const requestPromise = makeRequest(request, cacheKey, cacheDurationToUse, retryAttempts, retryDelay, signal)
  
  // Store pending request
  if (cacheKey) {
    pendingRequests.set(cacheKey, requestPromise)
    requestPromise.finally(() => pendingRequests.delete(cacheKey))
  }

  return requestPromise
}

/**
 * Check if request is allowed based on throttling rules
 */
const checkThrottling = (throttleType) => {
  const config = throttleConfig[throttleType] || throttleConfig.default
  const now = Date.now()
  const windowStart = now - config.window

  if (!requestHistory.has(throttleType)) {
    requestHistory.set(throttleType, [])
  }

  const history = requestHistory.get(throttleType)
  
  // Remove old requests outside the window
  const validRequests = history.filter(timestamp => timestamp > windowStart)
  requestHistory.set(throttleType, validRequests)

  if (validRequests.length >= config.limit) {
    const oldestRequest = Math.min(...validRequests)
    const waitTime = config.window - (now - oldestRequest)
    return { allowed: false, waitTime }
  }

  // Add current request to history
  validRequests.push(now)
  requestHistory.set(throttleType, validRequests)

  return { allowed: true, waitTime: 0 }
}

/**
 * Make the actual HTTP request with retry logic
 */
const makeRequest = async (request, cacheKey, cacheDuration, retryAttempts, retryDelay, signal) => {
  let lastError
  
  for (let attempt = 1; attempt <= retryAttempts; attempt++) {
    try {
      console.log(`🌐 API Request (attempt ${attempt}/${retryAttempts}):`, request.method?.toUpperCase(), 'URL hidden for security')

      const response = await axios(request)
      
      // Cache successful GET responses
      if (cacheKey && cacheDuration > 0) {
        cache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        })
        console.log('💾 Cached response:', cacheKey)
      }

      return response.data

    } catch (error) {
      lastError = error
      console.error(`❌ API Request failed (attempt ${attempt}/${retryAttempts}):`, error.message)
      
      // Don't retry on certain error types
      if (error.code === 'RATE_LIMIT_EXCEEDED' || 
          error.code === 'CANCELED' || 
          error.response?.status === 401 ||
          error.response?.status === 403) {
        break
      }
      
      // Retry with exponential backoff
      if (attempt < retryAttempts) {
        const delay = retryDelay * Math.pow(2, attempt - 1)
        console.log(`⏳ Retrying in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }
  
  // Handle final error
  return handleApiError(lastError)
}

/**
 * Handle API errors with user-friendly messages
 */
const handleApiError = (error) => {
  let userMessage = 'An unexpected error occurred. Please try again.'

  if (error.code === 'RATE_LIMIT_EXCEEDED') {
    userMessage = error.message
  } else if (error.code === 'CANCELED') {
    userMessage = 'Request was cancelled.'
  } else if (error.response) {
    const { status, data } = error.response
    
    switch (status) {
      case 400:
        userMessage = data?.message || 'Invalid request. Please check your input.'
        break
      case 401:
        userMessage = 'You are not authorized to perform this action.'
        break
      case 403:
        userMessage = 'Access denied. You don\'t have permission for this action.'
        break
      case 404:
        userMessage = 'The requested resource was not found.'
        break
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.'
        break
      case 500:
        userMessage = 'Server error. Please try again later.'
        break
      default:
        userMessage = data?.message || `Error ${status}: ${data?.error || 'Unknown error'}`
    }
  } else if (error.request) {
    userMessage = 'Network error. Please check your connection and try again.'
  }

  // Show error to user
  Alert.error(userMessage)

  // Log detailed error for debugging
  console.error('API Error Details:', {
    message: error.message,
    status: error.response?.status,
    data: error.response?.data,
    config: error.config
  })

  throw error
}

/**
 * Clear cache for specific key or all cache
 */
export const clearCache = (key = null) => {
  if (key) {
    cache.delete(key)
    console.log('🗑️ Cleared cache for:', key)
  } else {
    cache.clear()
    console.log('🗑️ Cleared all cache')
  }
}

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  const stats = {
    size: cache.size,
    keys: Array.from(cache.keys()),
    pendingRequests: pendingRequests.size
  }
  console.log('📊 Cache Stats:', stats)
  return stats
}

/**
 * Preload critical data for better performance
 */
export const preloadCriticalData = async () => {
  try {
    console.log('🚀 Preloading critical data...')
    
    // Only preload public data that doesn't require authentication
    // Remove endpoints that return 403 errors
  const criticalEndpoints = [
      // Add only public endpoints that work without authentication
      // { url: '/public/landingPage/fetchCommonInfo', method: 'get' },
      // { url: '/public/categories/fetchAll', method: 'get' }
    ]

    if (criticalEndpoints.length === 0) {
      console.log('ℹ️ No critical data to preload (requires authentication)')
      return
    }

    const preloadPromises = criticalEndpoints.map(endpoint => 
      enhancedHandler({
        url: endpoint.url,
        method: endpoint.method,
        cacheDuration: 600000, // 10 minutes for critical data
        throttleType: 'critical'
      }).catch(error => {
        console.warn('Preload failed for:', endpoint.url, error.message)
        return null
      })
    )

    await Promise.allSettled(preloadPromises)
    console.log('✅ Critical data preloaded')
  } catch (error) {
    console.warn('❌ Preload failed:', error.message)
  }
}

export default enhancedHandler
